import React, { useState } from 'react';
import { useStore } from '../../stores/useStore';
import { Client, ClientType, Priority } from '../../types';
import { Badge } from '../Common/Badge';

interface Props {
    client: Client;
    machineCount: number;
    onBack: () => void;
}

const CLIENT_TYPES: ClientType[] = ['업체', '자재'];
const PRIORITIES: Priority[] = ['일반', 'VIP', '주의'];

const priorityColors = {
    'VIP': 'yellow',
    '일반': 'gray',
    '주의': 'red',
} as const;

const sectionTitleStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 700,
    color: '#a09890',
    letterSpacing: '0.5px',
    marginBottom: '8px',
    textTransform: 'uppercase' as const,
};

const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
    fontSize: '14px',
    color: '#2c2c2c',
    padding: '6px 0',
    borderBottom: '1px solid #e8e0d5',
};

const labelStyle: React.CSSProperties = {
    minWidth: '60px',
    fontSize: '12px',
    color: '#a09890',
    paddingTop: '1px',
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: 'white',
    border: '1px solid #c4b8a5',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: "'Noto Sans KR', sans-serif",
    boxSizing: 'border-box',
    color: '#2c2c2c',
};

const fieldLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 700,
    color: '#5a5a5a',
    marginBottom: '4px',
    display: 'block',
};

const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%235a5a5a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '16px',
    paddingRight: '28px',
};

interface EditForm {
    name: string;
    type: ClientType;
    priority: Priority;
    phone: string;
    email: string;
    address: string;
    managerName: string;
    managerRole: string;
    managerPhone: string;
    notes: string;
}

interface MapApp {
    name: string;
    bg: string;
    textColor: string;
    getUrl: (addr: string) => string;
}

const MAP_APPS: MapApp[] = [
    {
        name: 'T맵',
        bg: '#E8344A',
        textColor: 'white',
        getUrl: (addr) => `tmap://route?goalname=${encodeURIComponent(addr)}`,
    },
    {
        name: '카카오맵',
        bg: '#FEE500',
        textColor: '#3C1E1E',
        getUrl: (addr) => `kakaomap://search?q=${encodeURIComponent(addr)}&type=address`,
    },
    {
        name: '네이버지도',
        bg: '#03C75A',
        textColor: 'white',
        getUrl: (addr) => `nmap://search?query=${encodeURIComponent(addr)}&appname=beomyangairtech`,
    },
    {
        name: '구글지도',
        bg: '#4285F4',
        textColor: 'white',
        getUrl: (addr) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr)}&travelmode=driving`,
    },
];

export const ClientDetail: React.FC<Props> = ({ client, machineCount, onBack }) => {
    const { updateClient } = useStore();
    const [showEditModal, setShowEditModal] = useState(false);
    const [mapAddress, setMapAddress] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<EditForm>({
        name: client.name,
        type: client.type,
        priority: client.priority,
        phone: client.contact.phone,
        email: client.contact.email,
        address: client.contact.address,
        managerName: client.managers[0]?.name ?? '',
        managerRole: client.managers[0]?.role ?? '',
        managerPhone: client.managers[0]?.phone ?? '',
        notes: client.notes,
    });

    const hasContact = client.contact.phone || client.contact.address || client.contact.email;

    const handleEditSubmit = () => {
        if (!editForm.name.trim()) return;

        const managers = editForm.managerName.trim()
            ? [{
                name: editForm.managerName.trim(),
                role: editForm.managerRole.trim() || '담당자',
                phone: editForm.managerPhone.trim(),
                email: client.managers[0]?.email ?? '',
            }]
            : [];

        updateClient(client.id, {
            name: editForm.name.trim(),
            type: editForm.type,
            priority: editForm.priority,
            contact: {
                phone: editForm.phone.trim(),
                email: editForm.email.trim(),
                address: editForm.address.trim(),
            },
            managers,
            notes: editForm.notes.trim(),
        });

        setShowEditModal(false);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 헤더 */}
            <div style={{
                backgroundColor: '#1e3a5f',
                color: 'white',
                padding: '14px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                        onClick={onBack}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            fontSize: '20px',
                            cursor: 'pointer',
                            padding: '0',
                            lineHeight: 1,
                        }}
                    >
                        ←
                    </button>
                    <span style={{ fontWeight: 700, fontSize: '16px' }}>거래처 상세</span>
                </div>
                <button
                    onClick={() => {
                        setEditForm({
                            name: client.name,
                            type: client.type,
                            priority: client.priority,
                            phone: client.contact.phone,
                            email: client.contact.email,
                            address: client.contact.address,
                            managerName: client.managers[0]?.name ?? '',
                            managerRole: client.managers[0]?.role ?? '',
                            managerPhone: client.managers[0]?.phone ?? '',
                            notes: client.notes,
                        });
                        setShowEditModal(true);
                    }}
                    style={{
                        background: 'none',
                        border: '1px solid rgba(255,255,255,0.5)',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        padding: '6px 12px',
                        borderRadius: '10px',
                    }}
                >
                    수정
                </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* 기본 정보 */}
                <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                            <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#2c2c2c', marginBottom: '6px' }}>
                                {client.name}
                            </h2>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                <Badge color={priorityColors[client.priority]} size="sm">
                                    {client.priority}
                                </Badge>
                                <span style={{
                                    fontSize: '11px',
                                    backgroundColor: '#e8e0d5',
                                    color: '#5a5a5a',
                                    padding: '2px 8px',
                                    borderRadius: '10px',
                                    fontWeight: 700,
                                }}>
                                    {client.type}
                                </span>
                            </div>
                        </div>
                        {client.outstandingAmount > 0 && (
                            <div style={{ textAlign: 'right' }}>
                                <p style={{ fontSize: '11px', color: '#a09890', marginBottom: '2px' }}>미수금</p>
                                <p style={{ fontSize: '18px', fontWeight: 700, color: '#c75c2e' }}>
                                    {client.outstandingAmount.toLocaleString()}원
                                </p>
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '16px', paddingTop: '12px', borderTop: '1px dashed #e8e0d5' }}>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '20px', fontWeight: 700, color: '#1e3a5f' }}>{machineCount}</p>
                            <p style={{ fontSize: '11px', color: '#a09890' }}>등록 기계</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '20px', fontWeight: 700, color: '#1e3a5f' }}>{client.managers.length}</p>
                            <p style={{ fontSize: '11px', color: '#a09890' }}>담당자</p>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '20px', fontWeight: 700, color: '#1e3a5f' }}>{client.transactionHistory.length}</p>
                            <p style={{ fontSize: '11px', color: '#a09890' }}>거래 내역</p>
                        </div>
                    </div>
                </div>

                {/* 연락처 */}
                {hasContact && (
                    <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', padding: '16px' }}>
                        <p style={sectionTitleStyle}>연락처</p>
                        {client.contact.phone && (
                            <div style={rowStyle}>
                                <span style={labelStyle}>전화</span>
                                <a href={`tel:${client.contact.phone}`} style={{ color: '#1e3a5f', fontWeight: 700, textDecoration: 'none' }}>
                                    {client.contact.phone}
                                </a>
                            </div>
                        )}
                        {client.contact.email && (
                            <div style={rowStyle}>
                                <span style={labelStyle}>이메일</span>
                                <span>{client.contact.email}</span>
                            </div>
                        )}
                        {client.contact.address && (
                            <div style={{ ...rowStyle, borderBottom: 'none' }}>
                                <span style={labelStyle}>주소</span>
                                <button
                                    onClick={() => setMapAddress(client.contact.address)}
                                    style={{
                                        flex: 1,
                                        background: 'none',
                                        border: 'none',
                                        padding: '0',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        color: '#1e3a5f',
                                        fontWeight: 700,
                                        textDecoration: 'underline',
                                        textDecorationColor: '#c4b8a5',
                                        fontFamily: "'Noto Sans KR', sans-serif",
                                    }}
                                >
                                    📍 {client.contact.address}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* 담당자 */}
                {client.managers.length > 0 && (
                    <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', padding: '16px' }}>
                        <p style={sectionTitleStyle}>담당자</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {client.managers.map((m, i) => (
                                <div key={i} style={{
                                    padding: '12px',
                                    backgroundColor: '#f4f1ea',
                                    border: '1px solid #e8e0d5',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                        <span style={{ fontWeight: 700, fontSize: '15px', color: '#2c2c2c' }}>{m.name}</span>
                                        <span style={{ fontSize: '12px', color: '#5a5a5a', backgroundColor: '#e8e0d5', padding: '2px 6px', borderRadius: '10px' }}>{m.role}</span>
                                    </div>
                                    {m.phone && (
                                        <a href={`tel:${m.phone}`} style={{ fontSize: '13px', color: '#1e3a5f', textDecoration: 'none', fontWeight: 700 }}>
                                            📞 {m.phone}
                                        </a>
                                    )}
                                    {m.email && (
                                        <p style={{ fontSize: '12px', color: '#5a5a5a', marginTop: '4px' }}>{m.email}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 메모 */}
                {client.notes && (
                    <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', padding: '16px' }}>
                        <p style={sectionTitleStyle}>메모</p>
                        <p style={{ fontSize: '14px', color: '#2c2c2c', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {client.notes}
                        </p>
                    </div>
                )}

                {/* 거래 내역 */}
                {client.transactionHistory.length > 0 && (
                    <div style={{ backgroundColor: 'white', border: '1px solid #c4b8a5', padding: '16px' }}>
                        <p style={sectionTitleStyle}>거래 내역</p>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {client.transactionHistory.map((t, i) => {
                                const isLast = i === client.transactionHistory.length - 1;
                                return (
                                    <div key={i} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px 0',
                                        borderBottom: isLast ? 'none' : '1px solid #e8e0d5',
                                    }}>
                                        <div>
                                            <p style={{ fontSize: '13px', color: '#2c2c2c', fontWeight: 700 }}>{t.description}</p>
                                            <p style={{ fontSize: '11px', color: '#a09890', marginTop: '2px' }}>{t.date} · {t.type}</p>
                                        </div>
                                        {t.amount > 0 && (
                                            <p style={{ fontSize: '14px', fontWeight: 700, color: '#1e3a5f' }}>
                                                {t.amount.toLocaleString()}원
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* 지도 앱 선택 시트 */}
            {mapAddress && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                    onClick={() => setMapAddress(null)}
                >
                    <div
                        style={{
                            backgroundColor: '#f4f1ea',
                            borderTop: '2px solid #1e3a5f',
                            width: '100%',
                            padding: '0 0 8px',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #e8e0d5' }}>
                            <p style={{ fontWeight: 700, fontSize: '14px', color: '#2c2c2c' }}>길찾기 앱 선택</p>
                            <p style={{ fontSize: '12px', color: '#a09890', marginTop: '3px' }}>{mapAddress}</p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', padding: '8px 16px', gap: '8px' }}>
                            {MAP_APPS.map((app) => (
                                <button
                                    key={app.name}
                                    onClick={() => {
                                        window.open(app.getUrl(mapAddress), '_blank');
                                        setMapAddress(null);
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: '14px 16px',
                                        backgroundColor: app.bg,
                                        border: 'none',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        width: '100%',
                                        textAlign: 'left',
                                    }}
                                >
                                    <span style={{ fontSize: '15px', fontWeight: 700, color: app.textColor, fontFamily: "'Noto Sans KR', sans-serif" }}>
                                        {app.name}
                                    </span>
                                    <span style={{ fontSize: '12px', color: app.textColor, opacity: 0.8 }}>길찾기</span>
                                </button>
                            ))}
                        </div>
                        <div style={{ padding: '0 16px 8px' }}>
                            <button
                                onClick={() => setMapAddress(null)}
                                style={{
                                    width: '100%',
                                    padding: '13px',
                                    border: '1px solid #c4b8a5',
                                    backgroundColor: 'white',
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    borderRadius: '10px',
                                    color: '#5a5a5a',
                                }}
                            >
                                취소
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 수정 모달 */}
            {showEditModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                    onClick={() => setShowEditModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: '#f4f1ea',
                            borderTop: '2px solid #c75c2e',
                            width: '100%',
                            maxHeight: '88vh',
                            overflowY: 'auto',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            backgroundColor: '#1e3a5f',
                            color: 'white',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <span style={{ fontWeight: 700, fontSize: '15px' }}>거래처 수정</span>
                            <button
                                onClick={() => setShowEditModal(false)}
                                style={{ background: 'none', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            <div>
                                <label style={fieldLabelStyle}>거래처명 *</label>
                                <input
                                    value={editForm.name}
                                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                    placeholder="예: 강남 빌딩"
                                    style={inputStyle}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={fieldLabelStyle}>유형</label>
                                    <select
                                        value={editForm.type}
                                        onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value as ClientType }))}
                                        style={selectStyle}
                                    >
                                        {CLIENT_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={fieldLabelStyle}>우선순위</label>
                                    <select
                                        value={editForm.priority}
                                        onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value as Priority }))}
                                        style={selectStyle}
                                    >
                                        {PRIORITIES.map((p) => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label style={fieldLabelStyle}>전화번호</label>
                                <input
                                    type="tel"
                                    value={editForm.phone}
                                    onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                                    placeholder="예: 02-1234-5678"
                                    style={inputStyle}
                                />
                            </div>

                            <div>
                                <label style={fieldLabelStyle}>주소</label>
                                <input
                                    value={editForm.address}
                                    onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
                                    placeholder="예: 서울특별시 강남구 ..."
                                    style={inputStyle}
                                />
                            </div>

                            <div style={{
                                border: '1px solid #c4b8a5',
                                padding: '12px',
                                backgroundColor: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                            }}>
                                <p style={{ fontSize: '12px', fontWeight: 700, color: '#5a5a5a' }}>담당자 (선택사항)</p>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={fieldLabelStyle}>이름</label>
                                        <input
                                            value={editForm.managerName}
                                            onChange={(e) => setEditForm((f) => ({ ...f, managerName: e.target.value }))}
                                            placeholder="홍길동"
                                            style={inputStyle}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={fieldLabelStyle}>직책</label>
                                        <input
                                            value={editForm.managerRole}
                                            onChange={(e) => setEditForm((f) => ({ ...f, managerRole: e.target.value }))}
                                            placeholder="시설팀장"
                                            style={inputStyle}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label style={fieldLabelStyle}>연락처</label>
                                    <input
                                        type="tel"
                                        value={editForm.managerPhone}
                                        onChange={(e) => setEditForm((f) => ({ ...f, managerPhone: e.target.value }))}
                                        placeholder="010-0000-0000"
                                        style={inputStyle}
                                    />
                                </div>
                            </div>

                            <div>
                                <label style={fieldLabelStyle}>메모</label>
                                <textarea
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                                    placeholder="특이사항, 결제 조건 등"
                                    style={{ ...inputStyle, height: '72px', resize: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '8px', paddingTop: '4px', paddingBottom: '8px' }}>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '13px',
                                        border: '1px solid #c4b8a5',
                                        backgroundColor: 'white',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        borderRadius: '10px',
                                        color: '#5a5a5a',
                                    }}
                                >
                                    취소
                                </button>
                                <button
                                    onClick={handleEditSubmit}
                                    disabled={!editForm.name.trim()}
                                    style={{
                                        flex: 2,
                                        padding: '13px',
                                        border: 'none',
                                        backgroundColor: editForm.name.trim() ? '#c75c2e' : '#c4b8a5',
                                        color: 'white',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        cursor: editForm.name.trim() ? 'pointer' : 'not-allowed',
                                        borderRadius: '10px',
                                    }}
                                >
                                    저장
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
